<div class="login-screen">
  <div class="login-brand-panel">
    <div class="brand-panel-glow"></div>
    <div class="brand-panel-content">
      <img src="/assets/logo.png" alt="Fork & Find" class="brand-logo" />
      <div class="brand-illustration">
        <i class="bx bx-shield-quarter"></i>
      </div>
      <h1 class="brand-heading">Secure your account</h1>
      <p class="brand-subheading">
        Choose a strong new password to keep your restaurant's data safe.
      </p>
    </div>
  </div>

  <div class="login-form-panel">
    <div class="signup-widget">
      <div class="logo">
        <img src="/assets/logo.png" alt="Company Logo" height="32" width="107" />
      </div>
      <div class="main">
        <div class="login-body">
          <div class="login-heading">
            <h2>Reset your password</h2>
            <p>Set a new password to continue to your dashboard.</p>
          </div>

          <form [formGroup]="resetPasswordForm" autocomplete="off" class="form">
            <div class="text-form-field">
              <label class="label">New password</label>
              <div class="password-input-wrapper input-with-icon">
                <i class="bx bx-lock-alt input-icon"></i>
                <input
                  [type]="showNewPassword ? 'text' : 'password'"
                  formControlName="new_password"
                  placeholder="Enter new password"
                  class="password"
                  [class.input-error]="
                    resetPasswordForm.get('new_password')?.invalid &&
                    (resetPasswordForm.get('new_password')?.dirty ||
                      resetPasswordForm.get('new_password')?.touched)
                  "
                  autocomplete="new-password"
                  (copy)="$event.preventDefault()"
                  (paste)="$event.preventDefault()"
                />
                <button
                  type="button"
                  class="password-toggle-btn"
                  (click)="togglePasswordVisibility('new')"
                  [attr.aria-label]="showNewPassword ? 'Hide password' : 'Show password'"
                >
                  <i [class]="showNewPassword ? 'bx bx-hide' : 'bx bx-show'"></i>
                </button>
              </div>

              @if (resetPasswordForm.get('new_password')?.value) {
                <div class="strength">
                  <div class="strength-bar">
                    <div
                      class="strength-bar-fill"
                      [class.strength-bar-fill--weak]="passwordStrengthLabel === 'Weak'"
                      [class.strength-bar-fill--fair]="passwordStrengthLabel === 'Fair'"
                      [style.width.%]="(passwordStrengthScore / 5) * 100"
                    ></div>
                  </div>
                  <span
                    class="strength-label"
                    [class.strength-label--weak]="passwordStrengthLabel === 'Weak'"
                    [class.strength-label--fair]="passwordStrengthLabel === 'Fair'"
                  >
                    {{ passwordStrengthLabel }}
                  </span>
                </div>
              }

              <ul class="requirements-list">
                <li [class.met]="passwordChecks.minLength">
                  <i
                    class="bx"
                    [class.bx-check-circle]="passwordChecks.minLength"
                    [class.bx-circle]="!passwordChecks.minLength"
                  ></i>
                  At least 8 characters
                </li>
                <li [class.met]="passwordChecks.upper && passwordChecks.lower">
                  <i
                    class="bx"
                    [class.bx-check-circle]="passwordChecks.upper && passwordChecks.lower"
                    [class.bx-circle]="!(passwordChecks.upper && passwordChecks.lower)"
                  ></i>
                  Upper &amp; lowercase letters
                </li>
                <li [class.met]="passwordChecks.number">
                  <i
                    class="bx"
                    [class.bx-check-circle]="passwordChecks.number"
                    [class.bx-circle]="!passwordChecks.number"
                  ></i>
                  At least one number
                </li>
                <li [class.met]="passwordChecks.symbol">
                  <i
                    class="bx"
                    [class.bx-check-circle]="passwordChecks.symbol"
                    [class.bx-circle]="!passwordChecks.symbol"
                  ></i>
                  A symbol (!&#64;#$%^&amp;*)
                </li>
              </ul>

              @if (
                resetPasswordForm.get('new_password')?.invalid &&
                (resetPasswordForm.get('new_password')?.dirty ||
                  resetPasswordForm.get('new_password')?.touched)
              ) {
                <div class="error">
                  <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
                  <p>{{ getPasswordErrorMessage() }}</p>
                </div>
              }
            </div>

            <div class="text-form-field">
              <label class="label">Confirm password</label>
              <div class="password-input-wrapper input-with-icon">
                <i class="bx bx-lock-alt input-icon"></i>
                <input
                  [type]="showConfirmPassword ? 'text' : 'password'"
                  formControlName="confirm_password"
                  placeholder="Confirm new password"
                  class="password"
                  [class.input-error]="
                    resetPasswordForm.get('confirm_password')?.invalid &&
                    (resetPasswordForm.get('confirm_password')?.dirty ||
                      resetPasswordForm.get('confirm_password')?.touched)
                  "
                  autocomplete="new-password"
                  (copy)="$event.preventDefault()"
                  (paste)="$event.preventDefault()"
                />
                <button
                  type="button"
                  class="password-toggle-btn"
                  (click)="togglePasswordVisibility('confirm')"
                  [attr.aria-label]="showConfirmPassword ? 'Hide password' : 'Show password'"
                >
                  <i [class]="showConfirmPassword ? 'bx bx-hide' : 'bx bx-show'"></i>
                </button>
              </div>

              @if (
                resetPasswordForm.get('confirm_password')?.errors?.['required'] &&
                (resetPasswordForm.get('confirm_password')?.dirty ||
                  resetPasswordForm.get('confirm_password')?.touched)
              ) {
                <div class="error">
                  <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
                  <p>Confirm password cannot be empty.</p>
                </div>
              } @else if (
                resetPasswordForm.errors?.['passwordsMismatch'] &&
                (resetPasswordForm.get('confirm_password')?.dirty ||
                  resetPasswordForm.get('confirm_password')?.touched)
              ) {
                <div class="error">
                  <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
                  <p>Confirm password should match the new password.</p>
                </div>
              }
            </div>

            <button
              type="button"
              class="btn-login"
              (click)="onSubmit()"
              [disabled]="!resetPasswordForm.valid"
            >
              Reset Password
              <i class="bx bx-right-arrow-alt"></i>
            </button>

            @if (errorMessage != '') {
              <div class="error">
                <mat-icon svgIcon="warning-icon" class="warning-icon"></mat-icon>
                <p>{{ errorMessage }}</p>
              </div>
            }
          </form>
        </div>
      </div>
      <div class="policy">
        <p>Copyright © {{ currentYear }} Fork & Find. All Rights Reserved.</p>
      </div>
    </div>
  </div>
</div>
